import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  contactApiClient,
  ContactMemoUpdateErrorResponse,
} from '@src/api/tenant/contact/contactApiClient';
import { Contact, ContactStatus } from '@src/api/types/contact';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import {
  ContactTypeBadge,
  STATUS_DOT_CLASSES,
  STATUS_LABELS,
  STATUS_OPTIONS,
} from '@src/features/tenant/contact/components/contactBadges';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { TENANT_URL } from '@src/routes/tenant.tsx';
import Button from '@src/ui/elements/button/Button';
import { ErrorMessage } from '@src/ui/elements/form';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

function BackToListLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className='flex items-center gap-2 py-[8px] text-xs text-primary-500 transition-opacity hover:opacity-60'
    >
      <svg
        width='12'
        height='12'
        viewBox='0 0 12 12'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M5.16667 6L9 9.83333L7.83333 11L2.83333 6L7.83333 1L9 2.16667L5.16667 6Z'
          fill='#2B5CDD'
        />
      </svg>
      {children}
    </button>
  );
}

function StatusToggle({
  status,
  onChange,
}: {
  status: ContactStatus;
  onChange: (status: ContactStatus) => void;
}) {
  return (
    <div className='inline-flex overflow-hidden rounded-md border border-solid border-gray-200'>
      {STATUS_OPTIONS.map((option, index) => {
        const isActive = option === status;
        return (
          <button
            key={option}
            type='button'
            onClick={() => onChange(option)}
            className={`flex items-center gap-2 px-3 py-[6px] text-sm transition-colors ${
              index > 0 ? 'border-l border-solid border-gray-200' : ''
            } ${isActive ? 'bg-primary-500 text-white' : 'bg-white text-gray-450'}`}
          >
            <span
              className={`size-2 shrink-0 rounded-full ${isActive ? 'bg-white' : STATUS_DOT_CLASSES[option]}`}
            />
            {STATUS_LABELS[option]}
          </button>
        );
      })}
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='flex border-b border-solid border-gray-200 py-4 last:border-b-0'>
      <dt className='w-[160px] shrink-0 text-sm text-gray-450'>{label}</dt>
      <dd className='flex-1 text-sm'>{children}</dd>
    </div>
  );
}

export default function ContactDetailForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [contact, setContact] = useState<Contact>();
  const [memo, setMemo] = useState('');
  const [memoErrors, setMemoErrors] = useState<ContactMemoUpdateErrorResponse>();
  const [isSavingMemo, setIsSavingMemo] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const isSavingMemoRef = useRef(false);
  const isUpdatingStatusRef = useRef(false);
  const displayedContactIdRef = useRef<number>();

  const loadContact = async () => {
    if (!id) {
      return;
    }

    setIsLoading(true);
    const res = await contactApiClient.show(Number(id));
    handleApiResponse(res, (data) => {
      setContact(data);
      setMemo(data.memo ?? '');
      displayedContactIdRef.current = data.id;
    });
    setIsLoading(false);
  };

  useEffect(() => {
    loadContact();
  }, [id]);

  const handleStatusChange = async (status: ContactStatus) => {
    if (!contact || isUpdatingStatusRef.current) {
      return;
    }

    isUpdatingStatusRef.current = true;
    setIsUpdatingStatus(true);
    try {
      const res = await contactApiClient.updateStatus(contact.id, status);
      handleApiResponse(res, () => {
        setContact((prev) => (prev ? { ...prev, status } : prev));
      });
    } finally {
      isUpdatingStatusRef.current = false;
      setIsUpdatingStatus(false);
    }
  };

  const handleMemoSave = async () => {
    if (!contact || isSavingMemoRef.current) {
      return;
    }

    const savingContactId = contact.id;
    isSavingMemoRef.current = true;
    setIsSavingMemo(true);
    setMemoErrors(undefined);

    try {
      const res = await contactApiClient.updateMemo(savingContactId, memo);
      if (displayedContactIdRef.current !== savingContactId) {
        // 保存中に別のお問い合わせへ画面遷移していた場合は、結果を反映しない
        return;
      }

      handleApiResponse(
        res,
        (data) => {
          setContact((prev) => (prev ? { ...prev, memo: data.memo } : prev));
          setMemo(data.memo ?? '');
          toast(TOAST_MESSAGE.UPDATE);
        },
        (errors) => {
          setMemoErrors(errors);
        },
      );
    } finally {
      isSavingMemoRef.current = false;
      setIsSavingMemo(false);
    }
  };

  return (
    <main>
      <SpinnerOverlay loading={isLoading || isUpdatingStatus} />
      <BackToListLink onClick={() => navigate(TENANT_URL.CONTACTS)}>
        お問い合わせ一覧へ戻る
      </BackToListLink>

      {contact && (
        <>
          <div className='mt-4 flex items-center justify-between rounded-md border border-solid border-gray-200 p-6'>
            <div>
              <h1 className='text-lg font-semibold'>{contact.company_name}</h1>
              <p className='mt-1 text-xs text-gray-450'>
                {contact.reception_number}・{contact.received_at}
              </p>
            </div>
            <StatusToggle
              status={contact.status}
              onChange={handleStatusChange}
            />
          </div>

          <div className='mt-6 rounded-md border border-solid border-gray-200 px-6'>
            <dl>
              <DetailRow label='種別'>
                <ContactTypeBadge type={contact.contact_type} />
              </DetailRow>
              <DetailRow label='対象サービス'>
                {contact.support_service_name && (
                  <span className='text-primary-500'>{contact.support_service_name}</span>
                )}
              </DetailRow>
              <DetailRow label='企業名'>{contact.company_name}</DetailRow>
              <DetailRow label='企業名（フリガナ）'>{contact.company_name_kana}</DetailRow>
              <DetailRow label='部署名'>{contact.department}</DetailRow>
              <DetailRow label='役職'>{contact.position}</DetailRow>
              <DetailRow label='氏名'>{contact.name}</DetailRow>
              <DetailRow label='氏名（フリガナ）'>{contact.name_kana}</DetailRow>
              <DetailRow label='メールアドレス'>
                <span className='text-primary-500'>{contact.email}</span>
              </DetailRow>
              <DetailRow label='電話番号'>{contact.phone_number}</DetailRow>
              <DetailRow label='お問い合わせ内容'>
                <p className='whitespace-pre-line'>{contact.content}</p>
              </DetailRow>
            </dl>
          </div>

          <div className='mt-6 rounded-md border border-solid border-gray-200'>
            <div className='border-b border-solid border-gray-200 bg-gray-50 px-6 py-3'>
              <h2 className='text-sm font-bold'>社内メモ</h2>
            </div>
            <div className='p-6'>
              <textarea
                value={memo}
                onChange={(event) => setMemo(event.target.value)}
                placeholder='社内向けのメモを入力してください。この内容はお客様には表示されません。'
                className={`h-[140px] w-full resize-none rounded border border-solid bg-[#FFFCF5] p-4 text-sm placeholder:text-gray-400 focus:outline-none ${
                  hasError(memoErrors?.memo) ? 'border-warning-500' : 'border-gray-250'
                }`}
              />
              <ErrorMessage>{firstError(memoErrors?.memo)}</ErrorMessage>
              <div className='mt-4 flex justify-end'>
                <Button
                  size='s'
                  onClick={handleMemoSave}
                  disabled={isSavingMemo}
                  loading={isSavingMemo}
                >
                  メモを保存
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
